import { jsxs, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { getYear, format, isAfter } from 'date-fns';
import { useState, useEffect } from 'react';
import { Button } from '../../../button/button/Button.mjs';
import getLocaleData from '../../../locale/getLocaleData.mjs';
import { useLinearLocale } from '../../../locale/LocaleContext.mjs';
import { useDatePickerContext } from '../../context/datePickerContext.mjs';
import { isSameYearMonth, isBeforeYearMonth, isAfterYearMonth, isAfterOrSameYearMonth, isBeforeOrSameYearMonth } from '../../utils/date.mjs';
import Header from './Header.mjs';
import { wrapper, monthButton } from './monthCalendar.vanilla.mjs';

const DATE_PICKER_MONTH_CALENDAR_NAME = "DatePickerMonthCalendar";
function MonthCalendar({
  __scopeDatePicker,
  onYearChange
}) {
  const [hoverMonth, setHoverMonth] = useState(void 0);
  const {
    mode,
    minDate,
    maxDate,
    startDate,
    endDate,
    selectedDate,
    onDateChange,
    onStartDateChange,
    onEndDateChange,
    excludeDates = [],
    openToDate
  } = useDatePickerContext(DATE_PICKER_MONTH_CALENDAR_NAME, __scopeDatePicker);
  const { locale } = useLinearLocale();
  const dateFnsLocaleData = getLocaleData(locale);
  const isRangeMode = mode === "range";
  const [year, setYear] = useState(() => {
    const nowYear = getYear(Date.now());
    if (isRangeMode) {
      return startDate !== null ? getYear(startDate) : nowYear;
    } else {
      return selectedDate !== null ? getYear(selectedDate) : nowYear;
    }
  });
  useEffect(() => {
    if (selectedDate !== null) {
      setYear(getYear(selectedDate));
    }
    if (isRangeMode && endDate !== null) {
      setYear(getYear(endDate));
    }
  }, [selectedDate, endDate, isRangeMode]);
  const getIsSelected = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (isRangeMode) {
      return getIsRangeStartDate(monthIndex) || getIsRangeEndDate(monthIndex);
    } else {
      return selectedDate !== null && isSameYearMonth(selectedDate, compareDate);
    }
  };
  const getIsRangeStartDate = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (isRangeMode && startDate !== null) {
      return isSameYearMonth(startDate, compareDate);
    }
    return false;
  };
  const getIsRangeEndDate = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (isRangeMode && endDate !== null) {
      return isSameYearMonth(endDate, compareDate);
    }
    return false;
  };
  const getIsInRange = (monthIndex) => {
    if (!isRangeMode) {
      return false;
    }
    const compareDate = new Date(year, monthIndex);
    if (startDate !== null && endDate !== null) {
      return isAfterOrSameYearMonth(compareDate, startDate) && isBeforeOrSameYearMonth(compareDate, endDate);
    }
    if (startDate !== null && endDate === null && hoverMonth !== void 0) {
      const hoveredMonthDate = new Date(year, hoverMonth);
      return isAfterYearMonth(compareDate, startDate) && isBeforeOrSameYearMonth(compareDate, hoveredMonthDate);
    }
  };
  const handleChangeMonthIndex = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    if (mode === "single") {
      onDateChange(compareDate);
    } else if (isRangeMode) {
      if (startDate !== null && endDate !== null) {
        onStartDateChange(compareDate);
        onEndDateChange(null);
        return;
      }
      if (startDate === null || isBeforeYearMonth(compareDate, startDate)) {
        onStartDateChange(compareDate);
      } else if (isSameYearMonth(compareDate, startDate) || isAfter(compareDate, startDate)) {
        onEndDateChange(compareDate);
      }
    }
  };
  const handleIncreaseYear = () => {
    const newYear = year + 1;
    setYear(newYear);
    onYearChange?.(newYear);
  };
  const handleDecreaseYear = () => {
    const newYear = year - 1;
    setYear(newYear);
    onYearChange?.(newYear);
  };
  const getIsDisabled = (monthIndex) => {
    const compareDate = new Date(year, monthIndex);
    const isExcludedYearMonth = excludeDates.find(
      (excludeDate) => isSameYearMonth(compareDate, excludeDate)
    ) !== void 0;
    const isUnderMinYearMonth = !isNil(minDate) && isBeforeYearMonth(compareDate, minDate);
    const isOverMaxYearMonth = !isNil(maxDate) && isAfterYearMonth(compareDate, maxDate);
    return isExcludedYearMonth || isUnderMinYearMonth || isOverMaxYearMonth;
  };
  const handleMouseEnter = (monthIndex) => {
    setHoverMonth(monthIndex);
  };
  const handleMouseLeave = () => {
    setHoverMonth(void 0);
  };
  return /* @__PURE__ */ jsxs("div", { style: { padding: "10px" }, children: [
    /* @__PURE__ */ jsx(
      Header,
      {
        year: openToDate ? getYear(openToDate) : year,
        onClickNextYear: handleIncreaseYear,
        onClickPrevYear: handleDecreaseYear
      }
    ),
    /* @__PURE__ */ jsx("div", { className: wrapper, children: Array.from({ length: 12 }).map((_, monthIndex) => {
      const disabled = getIsDisabled(monthIndex);
      const buttonProps = {
        key: monthIndex,
        children: format(new Date(year, monthIndex), "MMM", {
          locale: dateFnsLocaleData
        })
      };
      const isSelected = getIsSelected(monthIndex);
      return disabled ? /* @__PURE__ */ jsx(
        Button,
        {
          ...buttonProps,
          className: monthButton(),
          disabled: true,
          variant: "ghost",
          color: "grayDarker",
          "data-selected": isSelected
        }
      ) : /* @__PURE__ */ jsx(
        Button,
        {
          ...buttonProps,
          className: monthButton({
            isRangeStartDate: getIsRangeStartDate(monthIndex),
            isRangeEndDate: getIsRangeEndDate(monthIndex),
            isInRange: getIsInRange(monthIndex)
          }),
          variant: isSelected ? "solid" : "ghost",
          color: isSelected ? "green" : "grayDarker",
          "data-selected": isSelected,
          onClick: () => handleChangeMonthIndex(monthIndex),
          onPointerEnter: () => handleMouseEnter(monthIndex),
          onPointerLeave: handleMouseLeave
        }
      );
    }) })
  ] });
}

export { MonthCalendar as default };
//# sourceMappingURL=index.mjs.map
